import React, { useEffect, useRef, useState } from 'react';

/**
 * Reveal: wraps children and animates them when they enter the viewport.
 * Variants: up | down | left | right | scale | fade
 */
export default function Reveal({
  children,
  variant = 'up',
  delay = 0,
  duration = 750,
  threshold = 0.12,
  rootMargin = '0px 0px -60px 0px',
  className = '',
  as: Tag = 'div',
  once = true,
  style,
}) {
  const ref = useRef(null);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    // Honor reduced motion
    if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setShown(true);
      return;
    }

    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShown(true);
            if (once) obs.disconnect();
          } else if (!once) {
            setShown(false);
          }
        });
      },
      { threshold, rootMargin }
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, [threshold, rootMargin, once]);

  return (
    <Tag
      ref={ref}
      className={`reveal reveal-${variant} ${shown ? 'reveal-in' : ''} ${className}`}
      style={{
        transitionDelay: `${delay}ms`,
        transitionDuration: `${duration}ms`,
        ...style,
      }}
    >
      {children}
    </Tag>
  );
}
